import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

import { captureClient } from "./captureClient";

export const captureKeys = {
  /** Prefix for invalidating every capture query. */
  all: ["capture"] as const,
  timeline: (sessionKey: string, date: string) =>
    ["capture", sessionKey, "timeline", date] as const,
  flagged: (sessionKey: string, limit: number) =>
    ["capture", sessionKey, "flagged", limit] as const,
  nodes: (sessionKey: string) => ["capture", sessionKey, "nodes"] as const,
};

const NODES_REFETCH_MS = 30_000;

/**
 * Today's timeline is live — it answers "is this room capturing right now" —
 * so it polls; past days are immutable once judged, so `isToday: false` skips
 * polling entirely.
 */
export const useCaptureTimeline = (
  sessionKey: string,
  date: string,
  isToday: boolean,
) =>
  useSuspenseQuery({
    queryKey: captureKeys.timeline(sessionKey, date),
    queryFn: () => captureClient.timeline(date),
    refetchInterval: isToday ? 30_000 : false,
  });

export const useFlaggedVisits = (sessionKey: string, limit = 100) =>
  useSuspenseQuery({
    queryKey: captureKeys.flagged(sessionKey, limit),
    queryFn: () => captureClient.flagged(limit),
    refetchInterval: 30_000,
  });

/**
 * The capture-node roster (admin), suspending. Polls because a node's
 * online/recording state changes without a user action; `capture_node.*`
 * notification frames also invalidate this key for a faster refresh.
 */
export const useCaptureNodes = (sessionKey: string) =>
  useSuspenseQuery({
    queryKey: captureKeys.nodes(sessionKey),
    queryFn: () => captureClient.nodes(),
    refetchInterval: NODES_REFETCH_MS,
  });

/**
 * Non-suspense read of the same roster for the clinic board's room tiles: the
 * tile's capture dot is a nice-to-have, so a roster fetch that errors or is
 * slow must never suspend or blank the grid. Shares the cache entry (and the
 * socket-driven invalidation) with {@link useCaptureNodes}.
 */
export const useCaptureNodesQuery = (sessionKey: string) =>
  useQuery({
    queryKey: captureKeys.nodes(sessionKey),
    queryFn: () => captureClient.nodes(),
    refetchInterval: NODES_REFETCH_MS,
  });
